/* eslint-disable @typescript-eslint/no-explicit-any */
import { INotification, TNotificationId, TNotificationSelector } from './types';
import { EventEmitter, useMount, useLatest } from '@apia/util';
import { useState } from 'react';

export const onCloseNotificationCallbacks: Record<string, () => unknown> = {};

export type TNotificationType = 'danger' | 'success' | 'warning';

function shallowEqual(a: unknown[], b: unknown[]) {
  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;

  return true;
}

let id=0;
export function uniqueId() {
  return `notification_${id++}`
}

export type TDispatchedNotification = Omit<
  INotification<TNotificationType>,
  'id' | 'type' | 'isOpen'
> &
  Partial<Pick<INotification<TNotificationType>, 'id' | 'type'>>;

export class DefaultNotifier extends EventEmitter<{ changedList: boolean }> {
  notifications: INotification<TNotificationType>[] = [];

  areNotificationsOpen(): boolean {
    return !!document.querySelector('.notification');
  }

  shout() {
    this.emit('changedList', true);
  }

  close(id: TNotificationId | INotification): void {
    const actualId = typeof id === 'object' ? id.id : id;
    this.notifications = this.notifications.map((current) => {
      if (current.id === actualId) {
        current.onClose?.();
        return { ...current, isOpen: false };
      }
      return current;
    });
    this.shout();
  }

  closeAll(): void {
    this.notifications = this.notifications.map((current) => {
      current.onClose?.();
      return {
        ...current,
        isOpen: false,
      };
    });
    this.shout();
  }

  delete(id: TNotificationId | INotification): void {
    const actualId = typeof id === 'object' ? id.id : id;
    this.notifications = this.notifications.filter(
      (current) => current.id !== actualId,
    );
    this.shout();
  }

  useSelector = (
    selector: TNotificationSelector<TNotificationType>,
  ): INotification<TNotificationType>[] => {
    const [state, setState] = useState<INotification<TNotificationType>[]>([]);

    const lastState = useLatest(state);
    useMount(() => {
      return this.on('changedList', () => {
        const newState = selector(this.notifications);
        if (!shallowEqual(newState, lastState.current)) {
          setState([...newState]);
        }
      });
    });

    return state;
  };

  notify(notification: TDispatchedNotification): void {
    const id = notification.id ?? uniqueId();

    let hasAnimated = false;
    document
      .querySelectorAll('.notificationsFloatingList .notification__text')
      .forEach((current) => {
        if (current.textContent === notification.message) {
          const notificationElement = (current as HTMLElement).closest(
            '.notification',
          ) as HTMLElement;
          if (notificationElement) {
            notificationElement.classList.add('animate');
            setTimeout(() => {
              notificationElement.classList.remove('animate');
            }, 200);
            hasAnimated = true;
          } else
            console.warn(
              'A notification with that text was found, but it could not be animated',
            );
        }
      });
    if (hasAnimated) return;

    this.notifications.push({
      ...notification,
      isOpen: true,
      type: notification.type ?? 'warning',
      id,
    });

    this.shout();
  }

  on<K extends 'changedList'>(
    eventName: K,
    fn: (params: { changedList: boolean }[K]) => unknown,
  ): () => void {
    fn(true);
    return super.on(eventName, fn);
  }
}

export const defaultNotifier = new DefaultNotifier();

export function notify(notification: TDispatchedNotification) {
  defaultNotifier.notify(notification);
}
